/*
  enhancedProductCard:
  Similar to productCard except this product card allows for multiple products and swatches for each product
  
  - Parameters:
    - specialCollectionType: Specifies the type of product collection for enhanced display.
    - initialCurrentProductHandle: Handle of the initial product to display upon initialization.

  - Returns:
    - An object with properties and methods for managing the state and display of products within enhanced product cards.

  - Key Features:
    - updateCurrentProduct: Updates the displayed product details when a new product handle is selected.
    - shopifyData, featuredImage, secondImage, badges: Accessors that provide specific product data for display.
    - minPrice, cardDescription, enhancedTitle, shortenedTitle, collectionText, swatchImage: Computed properties that format and present product information in a user-friendly manner.
    - init: Asynchronously fetches and initializes product data based on the given collection type and initial product handle.
    - handleQuickAdd: Manages the display of a quick add dialog based on device screen size and product availability.

  This component enhances user interaction by dynamically displaying product information, supporting hover effects, and facilitating quick add actions, making it a versatile tool for product listings.
*/


function enhancedProductCard(specialCollectionType, initialCurrentProductHandle) {
  return {
    loading: true,
    error: '',
    products: [],
    currentProduct: {},
    quickAddOpen: false,
    hover: false,

    updateCurrentProduct: function (handle) {
      const product = this.products.find((p) => p.handle === handle);
      if (product) {
        this.currentProduct = product;
      }
    },

    get shopifyData() {
      return this.currentProduct;
    },

    get featuredImage() {
      return this.currentProduct.images[0];
    },

    get secondImage() {
      const {images} = this.currentProduct;
      if (images.length > 1) {
        return images[1];
      }
      return images[0];
    },

    get badges() {
      return this.shopifyData ? getProductBadges(this.shopifyData) : [];
    },

    get link() {
      if (!this.shopifyData) return '/';
      return `${window.Shopify.routes.root}products/${this.shopifyData.handle}`;
    },

    get minPrice() {
      minVarPrice = this.currentProduct.priceRangeFormatted.minVariantPrice;
      minVarPriceFormatted = formatPrice(minVarPrice.currencyCode, minVarPrice.amount);
      removedDecimalPrice = simplePrice(minVarPriceFormatted);
      return removedDecimalPrice;
    },

    get maxPrice() {
     const variants = this.shopifyData.variants;
     return simplePrice(variants[0].formattedPrice);
    },

    get maxCPrice() {
     const variants = this.shopifyData.variants;
     return simplePrice(variants[0].formattedCompareAtPrice);
    },

    get compareAtPrice() {
      // return JSON.stringify(this.currentProduct.compareAtPriceRangeFormatted);
      let compareVarPrice = this.currentProduct.compareAtPriceRangeFormatted.maxVariantPrice;
      let compareVarPriceFormatted = formatPrice(compareVarPrice.currencyCode, compareVarPrice.amount);
      let removedDecimalPrice = simplePrice(compareVarPriceFormatted);
      if(parseInt(compareVarPrice.amount) > 0) {
        return removedDecimalPrice;
      } else return '';
    },

    get price() {
      return this.shopifyData.priceRangeFormatted;
    },

    get priceRegular() {
      return this.shopifyData.compareAtPriceRangeFormatted;
    },

    get hasReducedPrice() {
      //const hammockPr = window.HAMMOCK_PRODUCT;
      const hammockPr = "the-hammock-throne";
      const { priceRegular, price } = this;
      if(this.shopifyData.handle == hammockPr){
        return true;
      }else{
        return priceRegular.minVariantPrice.amount > price.minVariantPrice.amount;
      } 
    },

    get cardDescription() {
      let cardDesc = this.currentProduct.cardDescription;
      const desc = this.currentProduct.description;
      if (cardDesc && cardDesc.length > 0) {
        return cardDesc;
      } else if (desc && desc.length > 50) {
        return desc.substring(0, 50) + '...';
      }
      return desc;
    },

    get enhancedTitle() {
      let enhancedTitle = this.currentProduct.enhancedTitle;
      if (enhancedTitle && enhancedTitle.length > 0) {
        return enhancedTitle;
      }
      return this.currentProduct.title;
    },

    get shortenedTitle() {
      const { enhancedTitle } = this.currentProduct;
      if (enhancedTitle && enhancedTitle.length > 0) {
        const ind = enhancedTitle.indexOf('/');
        return enhancedTitle.substring(0, ind - 1);
      } else return null;
    },

    get collectionText() {
      if (this.currentProduct.productType === 'Hanging Chair Hammock') {
        return 'Chair Hammock';
      } else if (this.currentProduct.productType === 'Cotton Rope Hammock') {
        return this.currentProduct.productType;
      } else if (this.currentProduct.productType === 'Classic Double Hammock') {
        return 'Signature Hammock';
      } else return null;
    },

    get swatchImage() {
      return this.currentProduct.swatchImage;
    },

    get disableSwatch() {
      return this.currentProduct.disableSwatch
    },

    get quickAddTitle() {
      const { shopifyData } = this;

      if (shopifyData.availableForSale) {
        return 'Quick Add';
      }

      if (shopifyData.tags.includes('Coming Soon') || shopifyData.seasonalCore?.value === 'Core') {
        return 'Notify Me';
      }

      return 'Sold Out';
    },

    get isSoldOut() {
      return this.quickAddTitle === 'Sold Out';
    },

    init: async function () {
      try {
        const enhancedProducts = await fetchEnhancedProductCard(specialCollectionType);
        this.products = enhancedProducts;

        // Initialize the current product
        if (initialCurrentProductHandle) {
          const initialProductIndex = this.products.findIndex((p) => p.handle === initialCurrentProductHandle);
          if (initialProductIndex > -1) {
            // Move the initial current product to the 0 position of the array
            this.products.unshift(this.products.splice(initialProductIndex, 1)[0]);
            this.currentProduct = this.products[0];
          }
        }
      } catch (error) {
        console.error('Error fetching enhanced product cards:', error);
      }
      this.loading = false;
    },

    handleQuickAdd() {
      if (window.innerWidth < 500) {
        this.$dispatch('open-quick-add-drawer', { product: this.currentProduct });
      } else {
        this.quickAddOpen = true;
      }
    },
  };
}

document.addEventListener('alpine:init', () => {
  Alpine.data('enhancedProductCard', enhancedProductCard);
});
